/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:20:01 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import {
  CONDITION_LOGIC_TYPES,
  CONDITION_OPERATORS,
  ICondition,
  FormConfiguration,
  FormField,
  ProcessTemplateArtifact,
  ProcessTemplateArtifactSchemaType,
  ProcessTemplateArtifactValidationSchema,
} from "lib/schemas";
import { WritableModel } from "lib/schemas/abstract";
import { UUID4 } from "lib/types";
import { schemaBuilder } from "lib/validation";

export enum DEPENDENCY_ACTIONS {
  show = "Show dependent field",
  hide = "Hide dependent field",
  require = "Make dependent field required",
  permit = "Restrict value of dependent field to preset values",
  default_value = "Set default value of dependent field",
  make_read_only = "Make dependent field read only",
  make_editable = "Make dependent field editable",
}

export interface FieldDependency extends ProcessTemplateArtifact, WritableModel<false> {
  form: FormConfiguration["id"];
  condition_logic_type: keyof typeof CONDITION_LOGIC_TYPES;
  field_dependency_conditions: FieldDependencyCondition[];
  field_dependency_actions: FieldDependencyAction[];
  field_dependency_condition_groups: FieldDependencyConditionGroup[];
}

export interface FieldDependencyConditionGroup extends ProcessTemplateArtifact, WritableModel<true> {
  condition_logic_type: keyof typeof CONDITION_LOGIC_TYPES;
}

export interface FieldDependencyCondition extends ProcessTemplateArtifact, WritableModel<false> {
  dependency: FieldDependency["id"];
  condition_form: FormConfiguration["id"];
  condition_field: FormField["id"];
  readonly condition_field_label?: FormField["label"];
  condition_operator: ICondition["condition_operator"];
  condition_value: ICondition["condition_value"];
  readonly condition_value_rendered?: string;
  condition_group?: FieldDependencyConditionGroup["id"];
}

export interface FieldDependencyAction extends ProcessTemplateArtifact, WritableModel<false> {
  dependency: FieldDependency["id"];
  dependent_field: FormField["id"];
  readonly dependent_field_label?: FormField["label"];
  dependent_field_action: keyof typeof DEPENDENCY_ACTIONS;
  action_permitted_values?: ActionPermittedValue[];
  readonly action_permitted_values_rendered?: (string | number | boolean)[];
  default_value?: string | number | boolean | { [key: string]: string | number | boolean };
}

interface ActionPermittedValue {
  id: UUID4;
  value: string | number | boolean;
}

const ActionPermittedValueValidationSchema: schemaBuilder.SchemaOf<ActionPermittedValue> = schemaBuilder
  .object()
  .shape({
    id: schemaBuilder.string().uuid().required("Id is required"),
    value: schemaBuilder.string().required("Value is required").nullable(),
  });

const FieldDependencyConditionValidationSchema: ProcessTemplateArtifactSchemaType<
  Omit<FieldDependencyCondition, "id" | "condition_field_label" | "condition_value_rendered" | "dependency">
> = ProcessTemplateArtifactValidationSchema.shape({
  condition_form: schemaBuilder
    .number()
    .required("Condition Form is required.")
    .typeError("Provide a valid condition form."),
  condition_field: schemaBuilder
    .number()
    .required("Condition Field is required.")
    .typeError("Provide a valid condition field."),
  condition_operator: schemaBuilder
    .mixed()
    .oneOf(Object.keys(CONDITION_OPERATORS))
    .required("Condition Operator is required.")
    .typeError("Provide a valid condition operator."),
  condition_value: schemaBuilder.string().optional().nullable(),
  condition_group: schemaBuilder.string().uuid().optional().nullable(),
});

const FieldDependencyActionValidationSchema: ProcessTemplateArtifactSchemaType<
  Omit<FieldDependencyAction, "id" | "dependent_field_label" | "action_permitted_values_rendered" | "dependency">
> = ProcessTemplateArtifactValidationSchema.shape({
  dependent_field: schemaBuilder
    .number()
    .required("Dependent Field is required.")
    .typeError("Provide a valid dependent field."),
  dependent_field_action: schemaBuilder
    .mixed()
    .oneOf(Object.keys(DEPENDENCY_ACTIONS))
    .required("Action is required.")
    .typeError("Provide a valid dependency action."),
  action_permitted_values: schemaBuilder.array(ActionPermittedValueValidationSchema).optional().nullable(),
  default_value: schemaBuilder.mixed().optional().nullable(),
});

const FieldDependencyConditionGroupValidationSchema: ProcessTemplateArtifactSchemaType<FieldDependencyConditionGroup> =
  ProcessTemplateArtifactValidationSchema.shape({
    id: schemaBuilder.string().uuid(),
    condition_logic_type: schemaBuilder
      .mixed()
      .oneOf(Object.keys(CONDITION_LOGIC_TYPES))
      .required("Condition Logic Type is required.")
      .typeError("Provide a valid condition logic type."),
  });

export const FieldDependencyValidationSchema = ProcessTemplateArtifactValidationSchema.shape({
  form: schemaBuilder.number().required("Form is required"),
  condition_logic_type: schemaBuilder.mixed().oneOf(Object.keys(CONDITION_LOGIC_TYPES)),
  field_dependency_conditions: schemaBuilder.array(FieldDependencyConditionValidationSchema).optional(),
  field_dependency_actions: schemaBuilder.array(FieldDependencyActionValidationSchema).optional(),
  field_dependency_condition_groups: schemaBuilder.array(FieldDependencyConditionGroupValidationSchema).optional(),
});
